export interface ChipOtherCategories {
    Factor: string;
    Tissue: string;
    Condition: string;
    Treatment: string;
    SampleType: string;
    /** This one should really be a number, but we have not enforced that */
    Replicate: string | number;
    ControlName?: string;
}

export interface ChipSampleGroupMember {
    id: string;
    name: string;
    other_categories: ChipOtherCategories;
    status?: string;
    files?: Array<{status?: string}>;
}

export interface DefaultChIPSampleGroup {
    group_metadata: Pick<ChipOtherCategories, 'Factor' | 'Tissue' | 'Condition' | 'Treatment' | 'SampleType'>;
    sample_ids: string[];
    /** Stored control sample IDs for IP groups; null means the default run has an invalid control mapping. */
    control_sample_ids: string[] | null;
}

export interface FunctionalEnrichmentSampleGroup {
    key: string;
    group_metadata: DefaultChIPSampleGroup['group_metadata'];
    samples: ChipSampleGroupMember[];
    controls: ChipSampleGroupMember[];
    disabled: boolean;
    disabledReason?: string;
}

/**
 * Simplified sample interface to describe what is required for the finding of replicates.
 */
interface SampleForReplicateFinding {
    name: string;
    other_categories: ChipOtherCategories;
}

const CHIP_GROUP_FIELDS = ['Factor', 'Tissue', 'Condition', 'Treatment', 'SampleType'] as const;

const getGroupKey = (sample: Pick<SampleForReplicateFinding, 'other_categories'>): string =>
    JSON.stringify(CHIP_GROUP_FIELDS.map(field => sample.other_categories?.[field]));

const hasValidGroupMetadata = (sample: Pick<SampleForReplicateFinding, 'other_categories'>): boolean =>
    CHIP_GROUP_FIELDS.every(field => typeof sample.other_categories?.[field] === 'string');

const hasValidReplicates = (group: SampleForReplicateFinding[]): boolean => {
    const replicates = group.map(sample => Number(sample.other_categories.Replicate));
    if (replicates.some(replicate => !Number.isInteger(replicate) || replicate < 1)) return false;

    const uniqueReplicates = new Set(replicates);
    return uniqueReplicates.size === replicates.length && Math.max(...replicates) === replicates.length;
};

const getDisplayGroupMetadata = (sample: Pick<SampleForReplicateFinding, 'other_categories'>): DefaultChIPSampleGroup['group_metadata'] => {
    const value = (field: (typeof CHIP_GROUP_FIELDS)[number]): string => {
        const category = sample.other_categories?.[field];
        return typeof category === 'string' ? category : '—';
    };

    return {
        Factor: value('Factor'),
        Tissue: value('Tissue'),
        Condition: value('Condition'),
        Treatment: value('Treatment'),
        SampleType: value('SampleType')
    };
};

const groupSamplesByMetadata = <T extends SampleForReplicateFinding>(samples: T[]): T[][] => {
    const groups = new Map<string, T[]>();
    samples.forEach(sample => {
        const key = getGroupKey(sample);
        const group = groups.get(key) ?? [];
        group.push(sample);
        groups.set(key, group);
    });

    return Array.from(groups.values());
};

const sortSampleGroups = <T extends SampleForReplicateFinding>(groups: T[][]): T[][] =>
    groups
        .map(group =>
            group.sort((a, b) => {
                const replicateDifference = Number(a.other_categories.Replicate) - Number(b.other_categories.Replicate);
                return Number.isNaN(replicateDifference) || replicateDifference === 0 ? a.name.localeCompare(b.name) : replicateDifference;
            })
        )
        .sort((a, b) => a[0].name.localeCompare(b[0].name));

/**
 * Builds selectable FE groups and disables groups that no longer exactly match
 * the samples used by the selected default ChIP analysis.
 */
export const buildFunctionalEnrichmentSampleGroups = (
    samples: ChipSampleGroupMember[],
    originalGroups: DefaultChIPSampleGroup[]
): FunctionalEnrichmentSampleGroup[] => {
    const currentGroups = sortSampleGroups(groupSamplesByMetadata(samples));
    const originalGroupsByIdSet = new Map(originalGroups.map(group => [JSON.stringify([...group.sample_ids].sort()), group] as const));
    const groupBySampleId = new Map(currentGroups.flatMap(group => group.map(sample => [sample.id, group] as const)));
    const samplesById = new Map(samples.map(sample => [sample.id, sample] as const));

    return currentGroups
        .filter(group => group[0].other_categories.SampleType === 'IP')
        .map(group => {
            const controlGroups = new Map<string, ChipSampleGroupMember[]>();
            const groupIdSetKey = JSON.stringify(group.map(sample => sample.id).sort());
            const originalGroup = originalGroupsByIdSet.get(groupIdSetKey);
            let missingControl = originalGroup?.control_sample_ids === null;

            originalGroup?.control_sample_ids?.forEach(controlId => {
                const control = samplesById.get(controlId);
                const controlGroup = control && groupBySampleId.get(control.id);
                if (!control || control.other_categories.SampleType !== 'Input' || !controlGroup) {
                    missingControl = true;
                    return;
                }
                controlGroups.set(getGroupKey(control), controlGroup);
            });

            const hasInvalidGroupMetadata =
                group.some(sample => !hasValidGroupMetadata(sample)) ||
                Array.from(controlGroups.values()).some(controlGroup => controlGroup.some(sample => !hasValidGroupMetadata(sample)));
            const hasInvalidReplicates =
                !hasValidReplicates(group) || Array.from(controlGroups.values()).some(controlGroup => !hasValidReplicates(controlGroup));
            const groupMatchesDefault = originalGroup !== undefined;
            const controlsMatchDefault = Array.from(controlGroups.values()).every(controlGroup =>
                originalGroupsByIdSet.has(JSON.stringify(controlGroup.map(sample => sample.id).sort()))
            );
            const disabled =
                hasInvalidGroupMetadata || hasInvalidReplicates || !groupMatchesDefault || missingControl || !controlsMatchDefault;

            return {
                key: getGroupKey(group[0]),
                group_metadata: getDisplayGroupMetadata(group[0]),
                samples: group,
                controls: Array.from(controlGroups.values()).flat(),
                disabled,
                disabledReason: disabled
                    ? hasInvalidGroupMetadata
                        ? 'This group has missing or invalid grouping metadata.'
                        : hasInvalidReplicates
                          ? 'This group has missing, duplicate, or invalid replicate values.'
                          : missingControl
                            ? 'A required control sample is missing.'
                            : 'This group no longer matches the selected default analysis.'
                    : undefined
            };
        });
};

/** Validates the current FE selection and returns the complete IP/control sample IDs to submit. */
export const resolveFunctionalEnrichmentGroupSelection = (
    selectedSampleIds: string[],
    samples: ChipSampleGroupMember[],
    originalGroups: DefaultChIPSampleGroup[]
): string[] => {
    const selectedIds = new Set(selectedSampleIds);
    const groups = buildFunctionalEnrichmentSampleGroups(samples, originalGroups);
    const selectedGroups = groups.filter(group => group.samples.some(sample => selectedIds.has(sample.id)));
    const selectedGroupIds = new Set(selectedGroups.flatMap(group => group.samples.map(sample => sample.id)));

    const invalid =
        selectedGroups.length === 0 ||
        selectedGroups.some(group => group.disabled || !group.samples.every(sample => selectedIds.has(sample.id))) ||
        selectedSampleIds.some(id => !selectedGroupIds.has(id));
    if (invalid) {
        throw new Error('Selected samples no longer match the selected default ChIP analysis.');
    }

    return Array.from(
        new Set(selectedGroups.flatMap(group => [...group.samples.map(sample => sample.id), ...group.controls.map(control => control.id)]))
    );
};

/**
 * Groups samples by their replicate properties (Factor, Tissue, Condition, Treatment, SampleType),
 * sorts within each group by replicate number (ascending), and sorts groups alphabetically by sample name.
 *
 * Validates that all replicates are present (1, 2, 3, ..., up to max) within each group and throws an error if not.
 * Also checks for duplicate replicates within groups.
 *
 * @param samples Array of samples to group and sort
 * @returns Array of sample arrays, each containing samples from one replicate group
 * @throws Error if replicates are missing or duplicated within any group
 */
export const groupAndSortSamplesByReplicates = <T extends SampleForReplicateFinding>(samples: T[]): Array<T[]> => {
    const groups = groupSamplesByMetadata(samples);

    const errors: string[] = [];
    groups.forEach(groupSamples => {
        const groupLabel = CHIP_GROUP_FIELDS.map(field => groupSamples[0].other_categories[field]).join('_');
        const replicates = groupSamples.map(s => {
            const rep = Number(s.other_categories.Replicate);
            if (!Number.isInteger(rep) || rep < 1) {
                errors.push(`Invalid replicate value "${s.other_categories.Replicate}" for sample "${s.name}" in group: "${groupLabel}"`);
            }
            return rep;
        });

        const replicateSet = new Set(replicates);
        const maxReplicate = Math.max(...Array.from(replicateSet));

        // Check for missing replicates (must have 1, 2, 3, ..., up to max)
        for (let i = 1; i <= maxReplicate; i++) {
            if (!replicateSet.has(i)) {
                const samplesInGroup = groupSamples.map(s => s.name).join(', ');
                errors.push(`Missing replicate "${i}" for group: "${groupLabel}" (samples: ${samplesInGroup})`);
            }
        }

        // Check for duplicate replicates
        if (replicateSet.size !== replicates.length) {
            const samplesInGroup = groupSamples.map(s => s.name).join(', ');
            errors.push(`Duplicate replicates found for group: "${groupLabel}" (samples: ${samplesInGroup})`);
        }
    });

    if (errors.length > 0) {
        throw new Error(errors.join('\n'));
    }

    return sortSampleGroups(groups);
};

/**
 * Given a set of selected sample IDs, expands the selection to include:
 * 1. All replicates of the selected samples.
 * 2. The corresponding "Input" control samples for each selected sample and its replicates.
 * 3. All replicates of the added control samples (this might be redundant).
 *
 * @returns Expanded array of sample IDs including replicates and controls
 */
export const expandSampleSelection = (selectedSampleIds: string[], samples: any[]): string[] => {
    const selectedIdsSet = new Set<string>(selectedSampleIds);

    // Some helper maps for quick lookup
    const sampleIdToGroup: Record<string, any[]> = {};
    groupAndSortSamplesByReplicates(samples).forEach(group => {
        group.forEach(sample => {
            sampleIdToGroup[sample.id] = group;
        });
    });
    const inputSampleNameToSample: Record<string, any> = {};
    samples.forEach(sample => {
        if (sample.other_categories?.SampleType !== 'Input') return;
        inputSampleNameToSample[sample.name] = sample;
    });
    const samplesById: Record<string, any> = {};
    samples.forEach(sample => {
        samplesById[sample.id] = sample;
    });

    // 1. Add replicates for initially selected samples
    selectedSampleIds.forEach(sampleId => {
        const group = sampleIdToGroup[sampleId];
        if (group) {
            group.forEach(sample => selectedIdsSet.add(sample.id));
        }
    });

    // 2. Add controls ("input"s) for each selected sample (including their replicates)
    const samplesAfterReplicates = Array.from(selectedIdsSet);
    samplesAfterReplicates.forEach(sampleId => {
        const sample = samplesById[sampleId];
        if (!sample) return;

        // Skip if already an Input sample
        if (sample.other_categories?.SampleType === 'Input') return;

        // Try to find Input sample with matching name
        const inputSample = inputSampleNameToSample[sample.other_categories?.ControlName];
        if (inputSample) {
            selectedIdsSet.add(inputSample.id);
        } else {
            console.warn(`No matching Input sample found for sample "${sample.name}" (ID: ${sample.id})`);
        }
    });

    // 3. Add controls' replicates (not sure if necessary, but for completeness)
    const samplesAfterControls = Array.from(selectedIdsSet);
    samplesAfterControls.forEach(sampleId => {
        const sample = samplesById[sampleId];
        if (!sample || sample.other_categories?.SampleType !== 'Input') return;

        const group = sampleIdToGroup[sampleId];
        if (group) {
            group.forEach(s => selectedIdsSet.add(s.id));
        }
    });

    return Array.from(selectedIdsSet);
};
